// =============================================================================
// Claude Powered Analytics — BaseQL resolvers
// =============================================================================
// runAnalyticsSkill (mutation) → starts an agent run, returns a runId immediately.
// getAnalyticsRunStatus (query) → polled by the UI for live steps + final report.
// Heavy lifting lives in server-only ../server/analytics-agent.js.
// =============================================================================

import { getContext } from '@adornis/baseql/server/context.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { logger } from '@adornis/base/logging.js';
import { startAnalyticsRun, getAnalyticsRun } from '../server/analytics-agent.js';
import { loadSkillsRegistry, listVault, readVaultFile, saveSkill, deleteSkill } from '../server/studio.js';
import { renderDocument, markdownToSpec } from '../server/docgen.js';
import type { DocFormat, DocSpec } from '../server/docgen.js';
import { listConnectionNames } from '../server/connections.js';

function requireAuth(): string {
  const ctx = getContext();
  const userID = (ctx as any)?.userID;
  if (!userID) throw new Error('AUTH_REQUIRED');
  return userID;
}

export const runAnalyticsSkill = registerMutation({
  type: () => String,
  operationName: 'runAnalyticsSkill',
  resolve: (skillId: string, question: string) => async () => {
    const userID = requireAuth();
    const id = String(skillId ?? '').slice(0, 40);
    const q = String(question ?? '').slice(0, 1000);
    const runId = startAnalyticsRun(id, q);
    logger.info({ userID, skillId: id, qLen: q.length, runId }, 'runAnalyticsSkill: started');
    return runId;
  },
  params: [
    { name: 'skillId', type: () => String },
    { name: 'question', type: () => String },
  ],
});

export const getAnalyticsRunStatus = registerQuery({
  type: () => String,
  operationName: 'getAnalyticsRunStatus',
  resolve: (runId: string) => async () => {
    requireAuth();
    const r = getAnalyticsRun(String(runId ?? ''));
    if (!r) {
      return JSON.stringify({ steps: [], done: true, report: '', error: 'run not found' });
    }
    return JSON.stringify({ steps: r.steps, done: r.done, report: r.report, error: r.error });
  },
  params: [{ name: 'runId', type: () => String }],
});

// ── Skill Studio (read-only registry + vault browser) ──────────────────────
export const listSkills = registerQuery({
  type: () => String,
  operationName: 'listSkills',
  resolve: () => async () => {
    requireAuth();
    return JSON.stringify(await loadSkillsRegistry());
  },
  params: [],
});

// Available connection NAMES (no secrets) — so the Skill Studio can show what
// a skill may reference in its connections field.
export const listConnections = registerQuery({
  type: () => String,
  operationName: 'listConnections',
  resolve: () => async () => {
    requireAuth();
    return JSON.stringify(await listConnectionNames());
  },
  params: [],
});

export const listVaultTree = registerQuery({
  type: () => String,
  operationName: 'listVaultTree',
  resolve: () => async () => {
    requireAuth();
    return JSON.stringify(await listVault());
  },
  params: [],
});

export const readVaultDoc = registerQuery({
  type: () => String,
  operationName: 'readVaultDoc',
  resolve: (folder: string, name: string) => async () => {
    requireAuth();
    return readVaultFile(String(folder ?? ''), String(name ?? ''));
  },
  params: [
    { name: 'folder', type: () => String },
    { name: 'name', type: () => String },
  ],
});

export const saveSkillOp = registerMutation({
  type: () => String,
  operationName: 'saveSkill',
  resolve: (skillJson: string) => async () => {
    requireAuth();
    return saveSkill(String(skillJson ?? ''));
  },
  params: [{ name: 'skillJson', type: () => String }],
});

export const deleteSkillOp = registerMutation({
  type: () => String,
  operationName: 'deleteSkill',
  resolve: (id: string) => async () => {
    requireAuth();
    return deleteSkill(String(id ?? ''));
  },
  params: [{ name: 'id', type: () => String }],
});

// ── General document export (Word / Excel / PDF) ───────────────────────────
// GENERAL, reusable: pass { markdown, title?, subtitle?, author? } OR { spec },
// plus a format. Returns JSON { filename, mime, base64 } for client-side download.
// Any feature (not just analytics) can call this.
export const exportDocument = registerMutation({
  type: () => String,
  operationName: 'exportDocument',
  resolve: (payloadJson: string, format: string) => async () => {
    requireAuth();
    const fmt: DocFormat = format === 'docx' || format === 'xlsx' ? (format as DocFormat) : 'pdf';
    let payload: any = {};
    try {
      payload = JSON.parse(payloadJson || '{}');
    } catch {
      /* ignore */
    }
    const spec: DocSpec =
      payload.spec ?? markdownToSpec(String(payload.markdown ?? ''), { title: payload.title, subtitle: payload.subtitle, author: payload.author });
    const doc = await renderDocument(spec, fmt);
    return JSON.stringify({ filename: doc.filename, mime: doc.mime, base64: doc.buffer.toString('base64') });
  },
  params: [
    { name: 'payloadJson', type: () => String },
    { name: 'format', type: () => String },
  ],
});
